/**
 * PerfOverlay — dev-only timeline panel.
 *
 * Mounted at the SPA root. Renders a fixed-bottom panel listing the marks +
 * measures from the last route navigation when `?perf=1` is in the URL AND
 * `PERF_ENABLED` is true (dev build or `VITE_PERF_PROFILE === "1"`).
 *
 * Rendered as a stacked bar — each phase row is a flex row whose width is
 * proportional to that phase's duration as a percentage of the total recipe
 * open. Marks (instantaneous events) render as a tick; measures render as a
 * filled track.
 *
 * Design (design.md §2 + §4):
 *   - Geist Mono, tabular-nums on numeric columns.
 *   - Hairline borders only — no shadows, no glassmorphism.
 *   - Warm neutrals from tokens.css (`--bg`, `--surface`, `--line`, `--text`,
 *     `--muted`).
 *   - Single accent on the primary phase bar — internal only, the overlay is
 *     never shown to users.
 *   - No banned vocabulary, no exclamations, no "Instrument" copy.
 *
 * Activation:
 *   PERF_ENABLED && location.search.includes("perf=1")
 *
 * In production builds the entire module is dead-code eliminated because
 * `PERF_ENABLED` is `false` and Rollup/Vite resolves the surrounding `if`.
 */

import { useEffect, useState } from "react";
import { PERF_ENABLED, subscribe, entries, type PerfEntry } from "./perf";

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatMs(ms: number): string {
  if (ms < 1) return "0";
  if (ms < 10) return ms.toFixed(1);
  return ms.toFixed(0);
}

function isPerfQueryActive(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get("perf") === "1";
  } catch {
    return false;
  }
}

// ── Style constants (warm neutrals, hairlines, mono) ───────────────────────────
//
// Inline-styled so the overlay has no external CSS coupling and can render
// even before tokens.css applies. All colours pull from tokens.css custom
// properties so dark mode auto-adapts.

const PANEL_STYLE: React.CSSProperties = {
  position: "fixed",
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 9999,
  background: "var(--surface, #fdfcfa)",
  color: "var(--text, #1a1714)",
  borderTop: "1px solid var(--line, #dcd7cc)",
  fontFamily: "var(--font-mono, 'Geist Mono', monospace)",
  fontSize: 11,
  lineHeight: 1.4,
  padding: "8px 12px",
  fontVariantNumeric: "tabular-nums",
  // No backdrop-filter, no shadow — hairline only per design.md §2.
};

const HEADER_STYLE: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  marginBottom: 6,
};

const HEADER_LABEL_STYLE: React.CSSProperties = {
  color: "var(--muted, #71685b)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  fontSize: 10,
};

const ROW_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  height: 18,
};

const NAME_CELL_STYLE: React.CSSProperties = {
  width: 180,
  flexShrink: 0,
  color: "var(--text, #1a1714)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const TRACK_CONTAINER_STYLE: React.CSSProperties = {
  flex: 1,
  position: "relative",
  height: 10,
  background: "var(--bg, #f7f5f1)",
  border: "1px solid var(--line, #dcd7cc)",
  borderRadius: 1,
  overflow: "hidden",
};

const VALUE_CELL_STYLE: React.CSSProperties = {
  width: 64,
  flexShrink: 0,
  textAlign: "right",
  color: "var(--muted, #71685b)",
};

// ── Component ──────────────────────────────────────────────────────────────────

export function PerfOverlay() {
  // Re-render flag — trips whenever a new entry lands. We read from `entries()`
  // each render to avoid retaining a stale snapshot in state.
  const [tick, setTick] = useState(0);
  const [active, setActive] = useState<boolean>(() => PERF_ENABLED && isPerfQueryActive());

  useEffect(() => {
    if (!PERF_ENABLED) return;
    // React to URL changes — popstate covers back/forward; pushState is the
    // SPA-internal nav signal we patch lightly so the overlay toggles when
    // the user changes ?perf in-flight. We only watch popstate here; the
    // typical activation flow is to load a URL with ?perf=1 directly.
    const onPop = () => {
      setActive(isPerfQueryActive());
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
    };
  }, []);

  useEffect(() => {
    if (!PERF_ENABLED) return;
    const unsub = subscribe(() => {
      setTick((t) => t + 1);
    });
    return unsub;
  }, []);

  if (!PERF_ENABLED) return null;
  if (!active) return null;

  const all = entries();
  // Re-read to depend on tick — silences "tick unused" while keeping the
  // re-render dependency explicit.
  void tick;

  // Compute timeline bounds: earliest mark startTime → latest mark/measure end.
  if (all.length === 0) {
    return (
      <div style={PANEL_STYLE} role="region" aria-label="recipe open timeline">
        <div style={HEADER_STYLE}>
          <span style={HEADER_LABEL_STYLE}>recipe open timeline</span>
          <span style={{ color: "var(--muted, #71685b)" }}>no entries yet</span>
        </div>
      </div>
    );
  }

  const minStart = Math.min(...all.map((e) => e.startTime));
  const maxEnd = Math.max(...all.map((e) => e.startTime + e.duration));
  const totalSpan = Math.max(maxEnd - minStart, 1); // avoid divide-by-zero

  // Render order: keep stable insertion order (the order in which boundaries
  // fired), so the timeline reads top-to-bottom in the order phases happened.
  const rows = all;

  return (
    <div style={PANEL_STYLE} role="region" aria-label="recipe open timeline">
      <div style={HEADER_STYLE}>
        <span style={HEADER_LABEL_STYLE}>recipe open timeline</span>
        <span style={{ color: "var(--muted, #71685b)" }}>
          {formatMs(totalSpan)} ms total · {rows.length} entries
        </span>
      </div>
      {rows.map((entry, i) => (
        <PerfRow
          key={`${entry.name}-${i}`}
          entry={entry}
          minStart={minStart}
          totalSpan={totalSpan}
        />
      ))}
    </div>
  );
}

// ── Row ────────────────────────────────────────────────────────────────────────

function PerfRow({
  entry,
  minStart,
  totalSpan,
}: {
  entry: PerfEntry;
  minStart: number;
  totalSpan: number;
}) {
  const offsetPct = ((entry.startTime - minStart) / totalSpan) * 100;
  const widthPct = entry.kind === "measure" ? Math.max((entry.duration / totalSpan) * 100, 0.4) : 1;
  const sinceStart = entry.startTime - minStart;

  // Single accent reserved for the primary phase track; marks render as a
  // hairline tick. Measures get the accent fill.
  const fillColor = entry.kind === "measure" ? "var(--accent, #c23d12)" : "var(--text, #1a1714)";

  return (
    <div style={ROW_STYLE}>
      <span style={NAME_CELL_STYLE} title={entry.name}>
        {entry.name}
      </span>
      <span style={TRACK_CONTAINER_STYLE} aria-hidden="true">
        <span
          style={{
            position: "absolute",
            left: `${offsetPct}%`,
            width: `${widthPct}%`,
            top: 0,
            bottom: 0,
            background: fillColor,
            opacity: entry.kind === "mark" ? 0.7 : 1,
          }}
        />
      </span>
      <span style={VALUE_CELL_STYLE}>
        {entry.kind === "measure" ? `${formatMs(entry.duration)} ms` : `+${formatMs(sinceStart)}`}
      </span>
    </div>
  );
}
